import type { TaskTemplate } from "../domain/entities.ts";
import type { SavedView, SidebarGroup, TaskListMode } from "../domain/app-types.ts";
import type { CollapsedBoardColumns } from "../features/tasks/board-columns.ts";
import type { WeekStart } from "../features/tasks/calendar-presentation.ts";
import type { MyDayPlans } from "../features/tasks/my-day.ts";
import { normalizeCreateTaskSaveMode } from "../features/tasks/task-panel-view.ts";
import { TaskCollectionState } from "../features/tasks/task-collection-state.ts";
import { TaskPanelState } from "../features/tasks/task-panel-state.ts";
import { TaskSelectionState } from "../features/tasks/task-selection-state.ts";
import { TaskViewState } from "../features/tasks/task-view-state.ts";
import { PlanningState } from "../features/tasks/planning-state.ts";
import { normalizeInboxTriage, type InboxTriageState } from "../features/tasks/inbox-triage.ts";
import { normalizeWeeklyReview, type WeeklyReviewReminder, type WeeklyReviewState } from "../features/tasks/weekly-review.ts";
import { CalendarState } from "../features/calendar/state.ts";
import { CollaborationRuntimeState } from "../features/collaboration/runtime-state.ts";
import { WorkspaceAccessState } from "../features/collaboration/workspace-access-state.ts";
import type { DestinationRef } from "../features/navigation/destinations.ts";
import { NavigationState } from "../features/navigation/state.ts";
import { defaultMobileActions, normalizeMobileActions } from "../features/navigation/qol-preferences.ts";
import type { SpaceTemplate } from "../features/navigation/space-template-controller.ts";
import { ShellDataState } from "./shell-data-state.ts";
import { SessionState } from "./session-state.ts";

export interface ApplicationStateSources {
  readMeta<T>(key: string, fallback: T): Promise<T>;
  localStorage: Pick<Storage, "getItem">;
  online: boolean;
  today(): string;
}

export async function loadApplicationState(sources: ApplicationStateSources) {
  const readMeta = sources.readMeta;
  const taskView = new TaskViewState(await readMeta<SavedView[]>("savedViews", []), await readMeta<string[]>("recentTaskSearches", []));
  const shellDataState = new ShellDataState();
  const workspaceAccess = new WorkspaceAccessState();
  const activeRootId = await readMeta<string | null>("activeRoot", null);
  const sessionState = new SessionState({
    activeRootId,
    activeCollection: await readMeta<{ id: string; rootId: string } | null>("activeCollection", null),
    syncState: sources.online ? "local" : "offline",
    personalTemplates: await readMeta<TaskTemplate[]>("personalTemplates", []),
    spaceTemplates: await readMeta<SpaceTemplate[]>("spaceTemplates", []),
    mobileActions: normalizeMobileActions((sources.localStorage.getItem("mobileActions") || defaultMobileActions.join(",")).split(",")),
  });
  const storedTaskListMode = await readMeta<TaskListMode | "titles">("taskListMode", "detailed");
  const taskCollectionState = new TaskCollectionState({
    collapsedColumns: await readMeta<CollapsedBoardColumns>("collapsedBoardColumns", {}),
    listMode: storedTaskListMode === "titles" ? "simple" : storedTaskListMode,
    boardMode: await readMeta<"simple" | "detailed">("boardCardMode", "detailed"),
    inactiveDays: await readMeta("boardInactiveDays", 0),
  });
  const collaborationState = new CollaborationRuntimeState();
  const calendarState = new CalendarState({
    mode: await readMeta<"day" | "week" | "month">("calendarMode", "month"),
    weekStart: await readMeta<WeekStart>("calendarWeekStart", "monday"),
    weekNumbers: await readMeta("calendarWeekNumbers", false),
    recurrenceProjections: await readMeta("calendarRecurrenceProjections", true),
    contextEnabled: await readMeta("externalCalendarContextEnabled", true),
    anchor: sources.today(),
  });
  const taskPanelState = new TaskPanelState({
    saveMode: normalizeCreateTaskSaveMode(await readMeta<string>("createTaskSaveMode", "close")),
    disclosures: await readMeta<Record<string, boolean>>("taskDisclosurePreferences", {}),
    reminderDefaults: await readMeta<Record<string, number[]>>("reminderDefaults", {}),
  });
  const taskSelection = new TaskSelectionState();
  const navigationState = new NavigationState({
    expandedGroup: await readMeta<SidebarGroup | null>("expandedSidebarGroup", null),
    expandedSpaceId: await readMeta<string | null>("expandedSpaceId", null),
    favoriteDestinations: await readMeta<DestinationRef[]>("favoriteDestinations", []),
    recentDestinations: await readMeta<DestinationRef[]>("recentDestinations", []),
    onboardingDismissed: await readMeta("onboardingDismissed", false),
    favoriteTaskIds: await readMeta<string[]>("favoriteTaskIds", []),
    recentTasks: await readMeta("recentTasks", []),
  });
  const planningState = new PlanningState({
    myDayPlans: await readMeta<MyDayPlans>("myDayPlans", {}),
    weeklyReview: normalizeWeeklyReview(await readMeta<WeeklyReviewState | null>("weeklyReviewState", null)),
    inboxTriage: normalizeInboxTriage(await readMeta<InboxTriageState | null>("inboxTriageState", null)),
    weeklyReviewReminder: await readMeta<WeeklyReviewReminder>("weeklyReviewReminder", { enabled: false, weekday: 5, hour: 17, lastPromptWeek: null }),
  });
  return { taskView, shellDataState, workspaceAccess, sessionState, taskCollectionState, collaborationState, calendarState, taskPanelState, taskSelection, navigationState, planningState };
}
